import "./TaskImages.scss";
import { useState, useRef, useEffect } from "react";
import { fetcher } from "../../utils/fetcher";
import ToolTip from "../ToolTip/ToolTip";
import Spinner from "../Spinner/Spinner";

function TaskImages({ taskData, onTaskUpdate }) {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const fileInputRef = useRef(null);
  const toolTipRef = useRef();

  const showToolTip = (e, arrow, content) => {
    const triggerElement = e.currentTarget;
    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  // Handle file upload
  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetcher.post(
        `/api/tasks/${taskData._id}/upload-image`,
        formData,
      );

      if (response.success) {
        // Update task data with new image
        const updatedTask = {
          ...taskData,
          images: [...(taskData.images || []), response.image],
        };

        if (onTaskUpdate) {
          onTaskUpdate(updatedTask);
        }
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image deletion
  const handleImageDelete = async (imageId, e) => {
    // Stop event propagation to prevent opening the image viewer
    e.stopPropagation();

    if (!confirm("Are you sure you want to delete this image?")) {
      return;
    }

    setDeletingImageId(imageId);

    try {
      const response = await fetcher.delete(
        `/api/tasks/${taskData._id}/images/${imageId}`,
      );

      if (response.success) {
        // Update task data by removing the deleted image
        const updatedTask = {
          ...taskData,
          images: taskData.images.filter((img) => img.id !== imageId),
        };

        if (onTaskUpdate) {
          onTaskUpdate(updatedTask);
        }
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      alert("Failed to delete image. Please try again.");
    } finally {
      setDeletingImageId(null);
    }
  };

  // Handle button click to trigger file input
  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input value to allow uploading the same file again
    e.target.value = "";
  };

  // Handle paste event
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          await handleImageUpload(file);
        }
        break;
      }
    }
  };

  // Open image viewer modal
  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  // Navigate to previous image
  const handlePreviousImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  // Navigate to next image
  const handleNextImage = () => {
    if (selectedImageIndex < (taskData.images?.length || 0) - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      handlePreviousImage();
    } else if (e.key === "ArrowRight") {
      handleNextImage();
    } else if (e.key === "Escape") {
      setSelectedImageIndex(null);
    }
  };

  // Close image viewer
  const handleCloseImageViewer = () => {
    setSelectedImageIndex(null);
  };

  // Auto-focus the overlay when image viewer opens for keyboard navigation
  useEffect(() => {
    if (selectedImageIndex !== null) {
      // Small delay to ensure the overlay is rendered
      setTimeout(() => {
        document.querySelector(".image-viewer-overlay")?.focus();
      }, 10);
    }
  }, [selectedImageIndex]);

  if (!taskData) {
    return (
      <div className="task-images">
        <div className="task-images-wrapper">
          <div className="placeholder">No task selected</div>
        </div>
      </div>
    );
  }

  const images = taskData.images || [];

  return (
    <div className="task-images" onPaste={handlePaste} tabIndex={0}>
      <ToolTip ref={toolTipRef} />

      {/* Upload Area */}
      <div className="upload-section">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />

        <div className="upload-area">
          <div className="upload-icon-wrapper">
            <span className="material-symbols-outlined upload-icon">
              cloud_upload
            </span>
          </div>

          <div className="upload-text">
            <p className="upload-title">Add Images</p>
            <p className="upload-description">
              Click to upload or paste from clipboard
            </p>
          </div>

          <button
            className="upload-button"
            onClick={handleUploadButtonClick}
            disabled={isUploading}
            onMouseEnter={(e) => showToolTip(e, "top-center", "Upload Image")}
            onMouseLeave={() => toolTipRef.current?.close()}
          >
            <span className="material-symbols-outlined">
              {isUploading ? "sync" : "add"}
            </span>
            {isUploading ? "Uploading..." : "Choose File"}
          </button>
        </div>

        <div className="upload-hint">
          <span className="material-symbols-outlined hint-icon">info</span>
          Supported: JPG, PNG, GIF (Max 5MB)
        </div>
      </div>

      {/* Divider */}
      {images.length > 0 && (
        <div className="section-divider">
          <span className="divider-text">
            Uploaded Images ({images.length})
          </span>
        </div>
      )}

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="images-grid">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="image-thumbnail"
              onClick={() => handleImageClick(index)}
              onMouseEnter={(e) => showToolTip(e, "top-center", image.filename)}
              onMouseLeave={() => toolTipRef.current?.close()}
            >
              <img src={image.thumbnailUrl} alt={image.filename} />
              <button
                className="delete-image-btn"
                onClick={(e) => handleImageDelete(image.id, e)}
                disabled={deletingImageId === image.id}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  showToolTip(e, "top-center", "Delete Image");
                }}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                {deletingImageId === image.id ? (
                  <span className="material-symbols-outlined spinning">
                    sync
                  </span>
                ) : (
                  <span className="material-symbols-outlined">close</span>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && !isUploading && (
        <div className="no-images">No images yet</div>
      )}

      {/* Full-size Image Viewer Modal */}
      {selectedImageIndex !== null && (
        <div
          className="image-viewer-overlay"
          onClick={handleCloseImageViewer}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div
            className="image-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="close-button"
              onClick={handleCloseImageViewer}
              onMouseEnter={(e) => showToolTip(e, "top-center", "Close (Esc)")}
              onMouseLeave={() => toolTipRef.current?.close()}
            >
              <span className="material-symbols-outlined">close</span>
            </button>

            {/* Previous button */}
            {selectedImageIndex > 0 && (
              <button
                className="nav-button prev-button"
                onClick={handlePreviousImage}
                onMouseEnter={(e) =>
                  showToolTip(e, "top-center", "Previous (←)")
                }
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            )}

            {/* Image */}
            <div className="image-container">
              <img
                src={images[selectedImageIndex].originalUrl}
                alt={images[selectedImageIndex].filename}
              />
            </div>

            {/* Next button */}
            {selectedImageIndex < images.length - 1 && (
              <button
                className="nav-button next-button"
                onClick={handleNextImage}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Next (→)")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            )}

            {/* Image info */}
            <div className="image-info">
              <div className="image-filename">
                {images[selectedImageIndex].filename}
              </div>
              <div className="image-counter">
                {selectedImageIndex + 1} / {images.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskImages;
